/**
 * When a Shopify Collabs affiliate link is used (landing URL includes dt_id),
 * keep that code on the cart and also apply MembershipFreeShipping.
 *
 * Package discounts still replace each other. A Collabs code is left in place
 * and the free-shipping code is added beside it. Both discounts must be set
 * to combine in Shopify admin or the shipping code stays inapplicable.
 */
(function (window, document) {
  'use strict';

  var FREE_SHIPPING_CODE = 'MembershipFreeShipping';
  var STORAGE_KEY = 'tw_collabs_codes';
  var VISIT_KEY = 'tw_collabs_visit';
  var STORAGE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  var PACKAGE_CODES = [
    '15OFFMONTHLY',
    '15OFFMONTHLY%',
    '15OFFMONTHLY_',
    '50OFFYEARLY',
    '50OFFYEARLY%',
    '50OFFYEARLY_',
    '150OFF2YEARS',
    '150OFF2YEARS%',
    '150OFF2YEARS_'
  ];

  var pending = Promise.resolve();

  function normalize(code) {
    return String(code || '').trim();
  }

  function keyOf(code) {
    return normalize(code).toUpperCase();
  }

  function cartEndpoint(file) {
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return String(root).replace(/\/?$/, '/') + file;
  }

  function enqueue(task) {
    pending = pending.then(task, task);
    return pending;
  }

  function captureVisit() {
    try {
      var dtId = new URLSearchParams(window.location.search).get('dt_id');
      if (dtId) {
        window.sessionStorage.setItem(VISIT_KEY, '1');
      }
    } catch (error) {}
  }

  function visitIsActive() {
    try {
      return window.sessionStorage.getItem(VISIT_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function readRemembered() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.codes) || !(parsed.exp > Date.now())) {
        window.localStorage.removeItem(STORAGE_KEY);
        return [];
      }
      return parsed.codes.map(normalize).filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  function writeRemembered(codes) {
    var unique = [];
    var seen = {};
    codes.forEach(function (code) {
      var key = keyOf(code);
      if (!key || seen[key] || key === keyOf(FREE_SHIPPING_CODE)) return;
      seen[key] = true;
      unique.push(normalize(code));
    });
    if (unique.length > 20) unique = unique.slice(unique.length - 20);
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ codes: unique, exp: Date.now() + STORAGE_TTL_MS })
      );
    } catch (error) {}
    return unique;
  }

  function readPackageSet() {
    var set = {};
    PACKAGE_CODES.forEach(function (code) {
      set[keyOf(code)] = true;
    });
    if (document && document.querySelectorAll) {
      document.querySelectorAll('[data-discount]').forEach(function (el) {
        var code = normalize(el.getAttribute('data-discount'));
        if (code) set[keyOf(code)] = true;
      });
    }
    return set;
  }

  function codesOnCart(cart) {
    var list = [];
    var entries = (cart && cart.discount_codes) || [];
    entries.forEach(function (entry) {
      var code = normalize(entry && entry.code);
      if (code) list.push(code);
    });
    return list;
  }

  function sameCodeList(left, right) {
    if (left.length !== right.length) return false;
    for (var i = 0; i < left.length; i += 1) {
      if (keyOf(left[i]) !== keyOf(right[i])) return false;
    }
    return true;
  }

  function rememberFromCart(cart, packageSet) {
    var remembered = readRemembered();
    if (!visitIsActive()) return remembered;

    var seen = {};
    remembered.forEach(function (code) {
      seen[keyOf(code)] = true;
    });

    var changed = false;
    codesOnCart(cart).forEach(function (code) {
      var key = keyOf(code);
      if (!key || seen[key] || packageSet[key] || key === keyOf(FREE_SHIPPING_CODE)) return;
      remembered.push(code);
      seen[key] = true;
      changed = true;
    });

    return changed ? writeRemembered(remembered) : remembered;
  }

  /**
   * @param {string[]} existing
   * @param {string} primary package code being applied, or '' when only stacking shipping
   * @param {string[]} collabsCodes
   * @param {Object<string, boolean>} packageSet
   * @returns {string[]}
   */
  function mergeDiscountCodes(existing, primary, collabsCodes, packageSet) {
    var collabsSet = {};
    (collabsCodes || []).forEach(function (code) {
      var key = keyOf(code);
      if (key) collabsSet[key] = true;
    });

    var primaryKey = keyOf(primary);
    var result = [];
    var seen = {};

    function push(code) {
      var key = keyOf(code);
      if (!key || seen[key]) return;
      seen[key] = true;
      result.push(normalize(code));
    }

    (existing || []).forEach(function (code) {
      var key = keyOf(code);
      if (!key || key === keyOf(FREE_SHIPPING_CODE)) return;
      if (primaryKey && packageSet[key] && key !== primaryKey) return;
      if (collabsSet[key] || key === primaryKey || !primaryKey) push(code);
    });

    if (primaryKey) push(primary);

    var hasCollabs = result.some(function (code) {
      return !!collabsSet[keyOf(code)];
    });
    if (hasCollabs) push(FREE_SHIPPING_CODE);
    return result;
  }

  function postDiscount(codes) {
    return fetch(cartEndpoint('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: codes.join(',') })
    }).then(function (res) {
      if (!res.ok) {
        return res.json().then(function (err) {
          return Promise.reject(err);
        });
      }
      return res.json();
    }).then(function (cart) {
      var shipping = ((cart && cart.discount_codes) || []).filter(function (entry) {
        return keyOf(entry && entry.code) === keyOf(FREE_SHIPPING_CODE);
      });
      if (shipping.length && shipping[0].applicable === false) {
        console.warn(
          'MembershipFreeShipping is on the cart but Shopify did not apply it. In Admin, allow the Collabs discount to combine with shipping discounts, and allow MembershipFreeShipping to combine with the Collabs discount.'
        );
      }
      return cart;
    });
  }

  function sync(primaryCode) {
    var primary = normalize(primaryCode);
    return fetch(cartEndpoint('cart.js'), { credentials: 'same-origin' })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var packageSet = readPackageSet();
        var collabsCodes = rememberFromCart(cart, packageSet);
        var existing = codesOnCart(cart);
        var next = mergeDiscountCodes(existing, primary, collabsCodes, packageSet);
        if (!next.length || sameCodeList(existing, next)) return cart;
        return postDiscount(next);
      })
      .catch(function (err) {
        console.warn('Tymewear: could not stack MembershipFreeShipping:', err);
      });
  }

  function shouldWatch() {
    return visitIsActive() || readRemembered().length > 0;
  }

  function ensure() {
    if (!shouldWatch()) return Promise.resolve();
    return enqueue(function () {
      return sync('');
    });
  }

  function applyAlongside(primaryCode) {
    var primary = normalize(primaryCode);
    if (!primary) return Promise.resolve();
    return enqueue(function () {
      return sync(primary);
    });
  }

  function checkoutDiscountValue(primaryCode) {
    var primary = normalize(primaryCode);
    var run = primary ? applyAlongside(primary) : ensure();
    return Promise.resolve(run).then(function (cart) {
      var codes = codesOnCart(cart);
      if (!codes.length && primary) codes = [primary];
      return codes.map(encodeURIComponent).join(',');
    });
  }

  captureVisit();

  function boot() {
    if (!shouldWatch()) return;
    ensure();
    window.setTimeout(ensure, 800);
    window.setTimeout(ensure, 2500);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        ensure();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.TymewearCollabsShipping = {
    FREE_SHIPPING_CODE: FREE_SHIPPING_CODE,
    applyAlongside: applyAlongside,
    checkoutDiscountValue: checkoutDiscountValue,
    ensure: ensure,
    _mergeDiscountCodes: mergeDiscountCodes
  };
})(window, document);
